import { Button, ConfirmationModal, Heading, LanguageSelector, style, CartIcon } from "@r360/library";
import classNames from "classnames";
import React, { useEffect, useId, useRef, useState } from "react";
import { useSelector, useStore } from "react-redux";
import { useLocation, useMatch } from "react-router-dom";
import { RootState } from "../..";
import packageInfo from "../../../package.json";
import { getNewOrModifiedCartProducts } from "../../BusinessUtils";
import { syncStateToBrowserStorage } from "../../configureStore";
import { productTypeNames } from "../../Constants";
import useQueryString from "../../hooks/QueryString/useQueryString";
import useAppDispatch from "../../hooks/useAppDispatch";
import useAppSelector from "../../hooks/useAppSelector";
import { useDynamicParams } from "../../hooks/useDynamicParams";
import useReservationTypeLink from "../../hooks/useReservationTypeLink";
import { useSendCartSignalOnUpdate } from "../../hooks/useSendCartSignalOnUpdate";
import useTranslate from "../../hooks/useTranslate";
import {
    activeReservationTypeSelector,
    cartCreatedFromReservationSelector,
    languagesSelector,
    optionsSelector,
} from "../../selectors/Selectors";
import { emptyCartProducts } from "../../store/actions/checkoutCart";
import { resetFilterState } from "../../store/actions/filter";
import { clearAllReservationTypeResults } from "../../store/actions/reservationResult";
import { resetLastSearchedTimestamps } from "../../store/actions/search";
import { setSignUpWrapper } from "../../store/actions/window";
import { TCartItem, TLanguage, TReservationType } from "../../store/types";
import Menu from "../Menu";
import MenuSearch from "../MenuSearch";
import PageContainer from "../PageContainer";
import confirmationDialog from "../Utilities/ConfirmationDialog/ConfirmationDialog";
import CartDropdown from "./CartDropdown";
import "./MainHeader.scss";
import { LoginMyPagesButton } from "../LoginMyPagesButton";

export const MainHeader: React.FC = () => {
    const location = useLocation();
    const store = useStore();
    const dispatch = useAppDispatch();
    const { pathname } = useLocation();
    const routeMatch = useMatch("/search/:reservationTypeId");
    const { reservationTypeId } = useDynamicParams(routeMatch?.params);
    const currentRouteId = reservationTypeId;
    const languages = useAppSelector(languagesSelector);
    const t = useTranslate();
    const lang = useAppSelector(state => state.clientData?.lang);
    const newDesignMyPages = useSelector(optionsSelector).layout?.new_design_my_pages;
    const isStandalone = useAppSelector(state => state.window.isStandalone);
    const showReservationTypeMenu = useSelector(optionsSelector).layout?.show_reservation_type_menu;
    const showLanguageSelector = useSelector(optionsSelector).layout?.show_language_selector;
    const showLoginMyPagesBtn = useSelector(optionsSelector).layout?.show_login_my_pages_button;
    const showCartBtn = useSelector(optionsSelector).layout?.show_cart_button;
    const checkoutCart = useAppSelector(state => state.checkoutCart);
    const [hiddenActionLinks, setHiddenActionLinks] = useState(false);
    const productsNotAddedPath = useAppSelector(state => state.checkoutCart.productsNotAddedPath);
    const [queryLang, setQueryLang] = useQueryString("lang");
    const loginSuccessful = useAppSelector(state => state.account.loginSuccessful);
    const cartProducts = getNewOrModifiedCartProducts(useAppSelector(state => state.checkoutCart?.products));
    const activeReservationType = useAppSelector(state => activeReservationTypeSelector(state, currentRouteId));
    const [reservationTypes, setReservationTypes] = useState<TReservationType[]>([]);
    const reservationResult = useAppSelector(state => state.reservationResult);
    const reservationTypesFromStore = useAppSelector(state => state.clientData.reservationtypes);
    const clientData = useAppSelector((state: RootState) => state.clientData);

    const reservationTypeLink = useReservationTypeLink();
    const { useAutoSearch } = (
        activeReservationType?.id
            ? clientData.reservationtypes.find((x: TReservationType) => x.id === activeReservationType.id)
            : {}
    ) as TReservationType;
    const isCheckout =
        (location.pathname.includes("/checkout") || location.pathname.includes("/pay")) &&
        clientData?.options?.layout?.new_design_checkout;
    const showMenuSearch = !(activeReservationType?.type === "SKIPASS" && useAutoSearch);
    const [changeLanguageConfirmation, setChangeLanguageConfirmation] = useState<{ show: boolean; language: string }>({
        show: false,
        language: "",
    });
    const { isIframe, isDesktop, isMobile, isTablet, iframeOffsetTop, isDev } = useAppSelector(
        (state: RootState) => state.window
    );
    const productsInCart = checkoutCart?.products?.length > 0;
    const cartCreatedFromReservation = useAppSelector(cartCreatedFromReservationSelector);
    const [isCartDropdownOpen, setIsCartDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const isModuleActive = useAppSelector(state => state.clientData?.isModuleActive);
    const clientVersion = packageInfo?.version;
    const isSu = clientData.isSu;
    // const maxRangeLength = [productTypeNames.ACCOMMODATION, productTypeNames.PACKAGE].includes(
    //     activeReservationType?.type || ""
    // )
    //     ? undefined // No datepicker range limit for accommodations and packages.
    //     : 30; // Limit datepicker range to 30 days for other reservation types to avoid performance issues when searching for long date ranges.

    const hasNoMainMenuElements =
        !showCartBtn && !showLanguageSelector && !showLoginMyPagesBtn && !showReservationTypeMenu && !isStandalone;

    useSendCartSignalOnUpdate();
    const cartButtonId = useId();
    const cartDropdownId = useId();

    useEffect(() => {
        setReservationTypes(() =>
            reservationTypesFromStore
                // .filter((item: TReservationType) => item.visible)
                .map((reservationType: TReservationType) => ({
                    ...reservationType,
                    link: reservationTypeLink(reservationType),
                    current: reservationType.id === activeReservationType?.id,
                }))
                .map((reservationType: TReservationType) => ({
                    ...reservationType,
                    current: reservationType.id === activeReservationType?.id,
                }))
        );
    }, [queryLang, activeReservationType, reservationResult, reservationTypesFromStore]);

    useEffect(() => {
        const hideActionLinks = (): boolean => {
            const hidePages = ["/user/login", "/user/registration"];
            if (location && hidePages.includes(pathname)) {
                return true;
            }
            return false;
        };
        setHiddenActionLinks(hideActionLinks());
    }, [location, pathname]);

    // Clear search results when language is changed or when page reloads
    useEffect(() => {
        // Language was changed via URL
        if (queryLang) {
            console.log("Language was changed to '" + queryLang + "', or page reloaded. Clear search results"); // KEEP
            dispatch(resetLastSearchedTimestamps());
        }
    }, [dispatch, queryLang]);

    // Handle click outside of dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsCartDropdownOpen(false);
            }
        };

        if (isCartDropdownOpen) {
            document.addEventListener("click", handleClickOutside, true);
        } else {
            document.removeEventListener("click", handleClickOutside, true);
        }

        return () => {
            document.removeEventListener("click", handleClickOutside, true);
        };
    }, [isCartDropdownOpen]);

    const changeLanguage = async (languageCode: string) => {
        if (productsInCart) {
            dispatch(emptyCartProducts());
        }

        dispatch(clearAllReservationTypeResults());
        dispatch(resetLastSearchedTimestamps());
        dispatch(resetFilterState());

        // Trigger sync of Redux state to browser storage explicitly to avoid race condition with throttle-time.
        syncStateToBrowserStorage(store)();

        setQueryLang(languageCode);
        window.location.reload();
    };

    const getCartItemsCount = () => {
        if (cartProducts) {
            return cartProducts.reduce((acc: number, item: TCartItem) => {
                if (item.count) {
                    return acc + item.count;
                }
                return acc + 1;
            }, 0);
        }
        return 0;
    };

    const cartItemsCount = getCartItemsCount();

    const getCheckoutHeaderTitle = () => {
        if (location.pathname.includes("/checkout/summary")) {
            return t("book.checkout.summary");
        }
        if (location.pathname.includes("/pay")) {
            return t("book.checkout.pay_order");
        }
        if (location.pathname.includes("/checkout")) {
            return t("book.checkout.connect_guests");
        }

        return activeReservationType?.description || "";
    };

    const handleLoginClick = async () => {
        if (productsNotAddedPath) {
            const response = await confirmationDialog({
                heading: t("book.leave_warning_header"),
                description: t("book.leave_warning_text"),
                confirmBtnText: t("book.leave_warning_confirm"),
                cancelBtnText: t("book.leave_warning_cancel"),
            });

            if (!response) {
                return;
            }
        }

        dispatch(setSignUpWrapper(true, true, false, newDesignMyPages ? "/my-pages" : "/account"));
    };

    const clearCache = () => {
        // This is async. Reload page after 3s to allow state in redux to be saved before clearing local storage.
        // See subscribe method in configureStore.js
        setTimeout(function () {
            // We only clear sessionStorage and keep localStorage
            let codebase, host;

            // Keep the same debugging variables after we have cleared session storage
            if (sessionStorage.getItem("__CODEBASE__")) {
                codebase = sessionStorage.getItem("__CODEBASE__");
            }

            if (sessionStorage.getItem("__HOST__")) {
                host = sessionStorage.getItem("__HOST__");
            }

            sessionStorage.clear();
            localStorage.clear();

            // Re-add debugging variables before reload
            if (codebase) {
                sessionStorage.setItem("__CODEBASE__", codebase);
            }

            if (host) {
                sessionStorage.setItem("__HOST__", host);
            }

            window.location.reload();
        }, 3000);

        alert("Cache is cleared, reloading page");
        return;
    };

    const renderActiveReservationTypeHeading = (additionalClassName = "") => {
        if (!activeReservationType) {
            return null;
        }

        return (
            <Heading type="h1" className={classNames("u-color-custom-primary-contrast", additionalClassName)}>
                {activeReservationType.description}
            </Heading>
        );
    };

    const renderMenu = () => {
        if (!isModuleActive) {
            return null;
        }

        // If on checkout page, render the checkout heading for that part of the checkout process.
        if (isCheckout) {
            return (
                <Heading onDarkBackground type="h1" className="u-pt-24 u-pt-lg-0">
                    {` ${t("book.checkout")}  `}
                    <span className="main-header-sub-heading">{getCheckoutHeaderTitle()}</span>
                </Heading>
            );
        }
        return <Menu reservationTypes={reservationTypes} />;
    };

    return (
        <>
            <ConfirmationModal
                open={!!changeLanguageConfirmation.language}
                heading={t("book.confirmations.change_language.title")}
                description={t("book.confirmations.change_language.description")}
                confirmBtnText={t("book.confirmations.change_language.ok")}
                cancelBtnText={t("book.general.cancel")}
                removing={true}
                onClose={() => setChangeLanguageConfirmation({ show: false, language: "" })}
                onConfirmClick={() => changeLanguage(changeLanguageConfirmation.language)}
                onCancelClick={() => setChangeLanguageConfirmation({ show: false, language: "" })}
                {...(isIframe && isDesktop && { fromTop: 120 + iframeOffsetTop + "px" })}
                {...(isIframe &&
                    (isMobile || isTablet) && {
                        fromTop: 80 + iframeOffsetTop + "px",
                    })}
            />
            <header className="main-header-wrapper">
                {(isDev || isSu) && (
                    <div className="main-header__clear-cache">
                        <Button type="tertiary" customColorsReversed id="r360client-version" onClick={clearCache}>
                            {clientVersion}
                        </Button>
                    </div>
                )}
                <PageContainer
                    minHeight={false}
                    className={location.pathname.includes("/login") && hasNoMainMenuElements ? "u-p-0" : ""}
                >
                    <div className={classNames("main-header", { "u-pt-0 u-pb-0": hasNoMainMenuElements })}>
                        {(!hasNoMainMenuElements || isCheckout) && (
                            <div className="main-header-menu">
                                {(isStandalone || showReservationTypeMenu || isCheckout) && renderMenu()}
                                <div className="main-header-right-section">
                                    <div className="main-header-language-select">
                                        {(isStandalone || showLanguageSelector) &&
                                            languages.length >= 2 &&
                                            languages.some(language => language.code === lang) && (
                                                <LanguageSelector
                                                    defaultLanguage={lang}
                                                    languageOptions={languages.map((language: TLanguage) => ({
                                                        value: language.code,
                                                        label: language.language,
                                                    }))}
                                                    onLanguageChange={(language: string) => {
                                                        productsInCart
                                                            ? setChangeLanguageConfirmation({ show: true, language })
                                                            : changeLanguage(language);
                                                    }}
                                                    showCountryCodeOnly={isMobile}
                                                />
                                            )}
                                    </div>
                                    {isModuleActive && (
                                        <div className="main-header-button-group">
                                            {(isStandalone || showCartBtn) && (
                                                <div className="main-header__cart-button" ref={dropdownRef}>
                                                    <Button
                                                        buttonClassName="main-header-button-group-button"
                                                        badgeCount={cartItemsCount > 0 ? cartItemsCount : null}
                                                        type="secondary"
                                                        rightIcon={
                                                            <CartIcon
                                                                color={
                                                                    cartItemsCount <= 0
                                                                        ? style.darkGreyColor
                                                                        : style.brandBlueColor
                                                                }
                                                            />
                                                        }
                                                        onClick={() => setIsCartDropdownOpen(prevState => !prevState)}
                                                        disabled={cartItemsCount <= 0 && !cartCreatedFromReservation}
                                                        aria-haspopup="menu"
                                                        aria-expanded={isCartDropdownOpen}
                                                        aria-controls={cartDropdownId}
                                                    >
                                                        {t("book.menu.cart")}
                                                    </Button>
                                                    {isCartDropdownOpen && (
                                                        <CartDropdown
                                                            onClose={() => setIsCartDropdownOpen(false)}
                                                            cartProducts={cartProducts}
                                                            id={cartDropdownId}
                                                            cartButtonId={cartButtonId}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                            {(isStandalone || showLoginMyPagesBtn) && !hiddenActionLinks && (
                                                <LoginMyPagesButton
                                                    loggedIn={loginSuccessful}
                                                    className="main-header-button-group-button"
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {activeReservationType && isModuleActive && (
                            <>
                                {renderActiveReservationTypeHeading()}
                                <MenuSearch
                                    reservationTypes={reservationTypes}
                                    showMenuSearch={showMenuSearch}
                                    onDarkBackground={true}
                                />
                            </>
                        )}
                    </div>
                </PageContainer>
            </header>
        </>
    );
};
